import React, { useState, useEffect } from 'react';
import {
    Card,
    Table,
    Button,
    Modal,
    Form,
    Input,
    Select,
    DatePicker,
    Tag,
    Space,
    message,
    Popconfirm,
    Row,
    Col,
    Statistic,
    Typography,
    Divider,
    Progress,
    Tooltip,
} from 'antd';
import {
    PlusOutlined,
    DownloadOutlined,
    ReloadOutlined,
    DeleteOutlined,
    PlayCircleOutlined,
    PauseCircleOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ClockCircleOutlined,
    DatabaseOutlined,
    FileOutlined,
    FolderOutlined,
    SettingOutlined,
} from '@ant-design/icons';
import api from '../../../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const Backups = () => {
    const [backups, setBackups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [stats, setStats] = useState({});
    const [filters, setFilters] = useState({});

    useEffect(() => {
        fetchBackups();
        fetchStats();
    }, [filters]);

    const fetchBackups = async () => {
        setLoading(true);
        try {
            const response = await api.adminGetBackups(filters);
            setBackups(response.data.data);
        } catch (error) {
            message.error('Failed to fetch backups');
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await api.adminGetBackupStats();
            setStats(response.data);
        } catch (error) {
            console.error('Failed to fetch stats:', error);
        }
    };

    const handleCreate = async (values) => {
        try {
            await api.adminStartBackup(values);
            message.success('Backup process started');
            setModalVisible(false);
            form.resetFields();
            fetchBackups();
            fetchStats();
        } catch (error) {
            message.error(error.message || 'Failed to start backup');
        }
    };

    const handleDelete = async (id) => {
        try {
            await api.adminDeleteBackup(id);
            message.success('Backup deleted successfully');
            fetchBackups();
            fetchStats();
        } catch (error) {
            message.error(error.message || 'Failed to delete backup');
        }
    };

    const handleDownload = async (id) => {
        try {
            const response = await api.adminDownloadBackup(id);
            // Handle file download
            message.success('Backup download started');
        } catch (error) {
            message.error(error.message || 'Failed to download backup');
        }
    };

    const handleRestore = async (id) => {
        try {
            await api.adminRestoreBackup(id);
            message.success('Backup restore initiated');
            fetchBackups();
        } catch (error) {
            message.error(error.message || 'Failed to restore backup');
        }
    };

    const handleCleanup = async () => {
        try {
            const response = await api.adminCleanupBackups();
            message.success(response.message);
            fetchBackups();
            fetchStats();
        } catch (error) {
            message.error(error.message || 'Failed to cleanup backups');
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed': return 'success';
            case 'in_progress': return 'processing';
            case 'failed': return 'error';
            case 'pending': return 'default';
            default: return 'default';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'completed': return <CheckCircleOutlined />;
            case 'in_progress': return <ClockCircleOutlined />;
            case 'failed': return <CloseCircleOutlined />;
            case 'pending': return <PauseCircleOutlined />;
            default: return null;
        }
    };

    const getTypeColor = (type) => {
        switch (type) {
            case 'database': return 'blue';
            case 'files': return 'green';
            case 'full': return 'purple';
            default: return 'default';
        }
    };

    const getTypeIcon = (type) => {
        switch (type) {
            case 'database': return <DatabaseOutlined />;
            case 'files': return <FileOutlined />;
            case 'full': return <FolderOutlined />;
            default: return null;
        }
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return 'Unknown';
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <div>
                    <Text strong>{text}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        {record.description || 'No description'}
                    </Text>
                </div>
            ),
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            render: (type) => (
                <Tag color={getTypeColor(type)} icon={getTypeIcon(type)}>
                    {type.toUpperCase()}
                </Tag>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
                    {status.toUpperCase()}
                </Tag>
            ),
        },
        {
            title: 'Size',
            dataIndex: 'file_size',
            key: 'file_size',
            render: (size) => formatFileSize(size),
        },
        {
            title: 'Duration',
            key: 'duration',
            render: (_, record) => {
                if (record.started_at && record.completed_at) {
                    const start = dayjs(record.started_at);
                    const end = dayjs(record.completed_at);
                    const duration = end.diff(start, 'second');
                    return `${duration}s`;
                }
                return '-';
            },
        },
        {
            title: 'Created',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (date) => dayjs(date).format('MMM DD, YYYY HH:mm'),
        },
        {
            title: 'Triggered By',
            dataIndex: 'triggered_by',
            key: 'triggered_by',
            render: (triggeredBy) => (
                <Tag color={triggeredBy === 'manual' ? 'blue' : 'green'}>
                    {triggeredBy?.toUpperCase() || 'UNKNOWN'}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    {record.status === 'completed' && (
                        <>
                            <Button
                                size="small"
                                icon={<DownloadOutlined />}
                                onClick={() => handleDownload(record.id)}
                            >
                                Download
                            </Button>
                            <Popconfirm
                                title="Are you sure you want to restore this backup? This will overwrite current data."
                                onConfirm={() => handleRestore(record.id)}
                                okText="Yes"
                                cancelText="No"
                            >
                                <Button
                                    size="small"
                                    type="primary"
                                    icon={<ReloadOutlined />}
                                >
                                    Restore
                                </Button>
                            </Popconfirm>
                        </>
                    )}
                    <Popconfirm
                        title="Are you sure you want to delete this backup?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                        >
                            Delete
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div className="mb-6">
                <Title level={2}>Backup & Restore</Title>
                <Text type="secondary">
                    Manage database and file backups
                </Text>
            </div>

            {/* Statistics */}
            <Row gutter={16} className="mb-6">
                <Col span={4}>
                    <Card>
                        <Statistic
                            title="Total Backups"
                            value={stats.total || 0}
                        />
                    </Card>
                </Col>
                <Col span={4}>
                    <Card>
                        <Statistic
                            title="Completed"
                            value={stats.completed || 0}
                            valueStyle={{ color: '#3f8600' }}
                        />
                    </Card>
                </Col>
                <Col span={4}>
                    <Card>
                        <Statistic
                            title="Failed"
                            value={stats.failed || 0}
                            valueStyle={{ color: '#cf1322' }}
                        />
                    </Card>
                </Col>
                <Col span={4}>
                    <Card>
                        <Statistic
                            title="In Progress"
                            value={stats.in_progress || 0}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col span={4}>
                    <Card>
                        <Statistic
                            title="Total Size"
                            value={formatFileSize(stats.total_size || 0)}
                        />
                    </Card>
                </Col>
                <Col span={4}>
                    <Card>
                        <Statistic
                            title="Auto Backups"
                            value={stats.auto_backups || 0}
                        />
                    </Card>
                </Col>
            </Row>

            <Card>
                <div className="mb-4 flex justify-between items-center">
                    <Title level={4} className="!mb-0">All Backups</Title>
                    <Space>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={fetchBackups}
                        >
                            Refresh
                        </Button>
                        <Popconfirm
                            title="Are you sure you want to cleanup expired backups?"
                            onConfirm={handleCleanup}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button
                                danger
                                icon={<DeleteOutlined />}
                            >
                                Cleanup Expired
                            </Button>
                        </Popconfirm>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setModalVisible(true)}
                        >
                            Create Backup
                        </Button>
                    </Space>
                </div>

                {/* Filters */}
                <div className="mb-4">
                    <Row gutter={16}>
                        <Col span={6}>
                            <Select
                                placeholder="Filter by type"
                                allowClear
                                style={{ width: '100%' }}
                                onChange={(value) => setFilters({ ...filters, type: value })}
                            >
                                <Option value="database">Database</Option>
                                <Option value="files">Files</Option>
                                <Option value="full">Full</Option>
                            </Select>
                        </Col>
                        <Col span={6}>
                            <Select
                                placeholder="Filter by status"
                                allowClear
                                style={{ width: '100%' }}
                                onChange={(value) => setFilters({ ...filters, status: value })}
                            >
                                <Option value="pending">Pending</Option>
                                <Option value="in_progress">In Progress</Option>
                                <Option value="completed">Completed</Option>
                                <Option value="failed">Failed</Option>
                            </Select>
                        </Col>
                        <Col span={6}>
                            <Select
                                placeholder="Filter by trigger"
                                allowClear
                                style={{ width: '100%' }}
                                onChange={(value) => setFilters({ ...filters, is_auto: value })}
                            >
                                <Option value={false}>Manual</Option>
                                <Option value={true}>Automatic</Option>
                            </Select>
                        </Col>
                        <Col span={6}>
                            <RangePicker
                                style={{ width: '100%' }}
                                onChange={(dates) => {
                                    if (dates) {
                                        setFilters({
                                            ...filters,
                                            date_from: dates[0]?.format('YYYY-MM-DD'),
                                            date_to: dates[1]?.format('YYYY-MM-DD'),
                                        });
                                    } else {
                                        setFilters({
                                            ...filters,
                                            date_from: undefined,
                                            date_to: undefined,
                                        });
                                    }
                                }}
                            />
                        </Col>
                    </Row>
                </div>

                <Table
                    columns={columns}
                    dataSource={backups}
                    loading={loading}
                    rowKey="id"
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} of ${total} backups`,
                    }}
                />
            </Card>

            <Modal
                title="Create Backup"
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                width={500}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleCreate}
                >
                    <Form.Item
                        name="type"
                        label="Backup Type"
                        rules={[{ required: true, message: 'Please select backup type' }]}
                    >
                        <Select placeholder="Select backup type">
                            <Option value="database">Database Only</Option>
                            <Option value="files">Files Only</Option>
                            <Option value="full">Full Backup (Database + Files)</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="name"
                        label="Backup Name"
                    >
                        <Input placeholder="Leave empty for auto-generated name" />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="Description"
                    >
                        <TextArea rows={3} placeholder="Backup description (optional)" />
                    </Form.Item>

                    <Form.Item className="mb-0">
                        <Space>
                            <Button type="primary" htmlType="submit">
                                Create Backup
                            </Button>
                            <Button onClick={() => setModalVisible(false)}>
                                Cancel
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default Backups;
